import React from 'react';
import { List, Datagrid, TextField, Filter, TextInput, SelectInput, EditButton, FunctionField, TopToolbar, CreateButton } from 'react-admin';
import { BillingAccountDeleteListButton } from './BillingAccountDeleteListButton';
import { isBillingAccountActive } from '../../Services/billingAccount';
import WarningIcon from '@mui/icons-material/Warning';
import CheckIcon from '@mui/icons-material/Check';
import { green, red } from '@mui/material/colors';
import { Chip } from '@mui/material';

// ⚠️ « Free » désigne la CASE À COCHER de la fiche, pas un calcul. Depuis
// l'abandon de l'offre gratuite, un compte n'est gratuit que parce qu'on l'a
// déclaré tel ; les comptes sans abonnement sont des reliquats et se cherchent
// dans le menu État. Deux « Free » de sens différents dans le même écran étaient
// illisibles.
const kindChoices = [
	{ id: 'contract', name: 'Entreprise' },
	{ id: 'payasyougo', name: 'Pay as you go' },
	{ id: 'free', name: 'Free' },
];

// Cumulable avec le type : un compte sans projet — ou silencieux depuis un mois
// — peut être gratuit comme sous contrat. C'est le croisement qui compte : un
// compte Entreprise sans activité depuis 30 jours ne se traite pas comme une
// inscription abandonnée.
//
// « Sans abonnement » est l'ancienne nature « Free » calculée : ni contrat, ni
// abonnement payant. Elle est devenue un état le jour où « Free » a désigné la
// case à cocher.
//
// « Sans activité » = aucun traitement de connecteur ET aucune session MCP sur
// 30 jours. Le flux d'activité est volontairement écarté : il compte les
// paiements automatiques comme de l'activité (cf. noActivitySQL côté serveur).
const stateChoices = [
	{ id: 'noSubscription', name: 'Sans abonnement' },
	{ id: 'orphan', name: 'Sans projet (orphelins)' },
	{ id: 'noActivity', name: 'Sans activité (30 j)' },
];

const BillingAccountFilter = (props: any) => (
	<Filter {...props}>
		<TextInput label="Search" source="q" alwaysOn />
		<SelectInput label="Type" source="kind" choices={kindChoices} alwaysOn emptyText="Tous" />
		<SelectInput label="État" source="state" choices={stateChoices} alwaysOn emptyText="Tous" />
	</Filter>
);

// Seul un compte ENTREPRISE se crée ici : un compte Pay as you go naît d'un paiement.
const ListActions = () => (
	<TopToolbar>
		<CreateButton label="Nouveau compte Entreprise" />
	</TopToolbar>
);

export const BillingAccountList = (props: any) => (
	<List {...props} filters={<BillingAccountFilter />} perPage={50} actions={<ListActions />}>
		<Datagrid rowClick={(id, basePath, record) => `${record.id}`}>
			<TextField source="name" />
			<TextField source="organisationName" />
			<TextField source="city" />
			<FunctionField
				label="Type"
				render={(record) =>
					record.billingMode === 'contract' ? (
						<Chip size="small" color="primary" label="Entreprise" />
					) : (
						<Chip size="small" label="Pay as you go" />
					)
				}
			/>
			{/* Colonne à part, et pas un badge collé au type : « Free » ne qualifie
			    pas le mode de facturation mais le fait que Quanti ne facture pas ce
			    compte — un compte gratuit reste Entreprise ou Pay as you go. */}
			<FunctionField
				label="Free"
				render={(record) =>
					record.isFree ? (
						<CheckIcon
							style={{ color: green[500] }}
							titleAccess="Compte gratuit : démo ou essai interne"
						/>
					) : null
				}
			/>
			<FunctionField
				label="Active"
				render={(record) =>
					record.billingMode === 'contract' || isBillingAccountActive(record) ? (
						<CheckIcon style={{ color: green[500] }} titleAccess="Active subscription" />
					) : (
						<WarningIcon style={{ color: red[500] }} titleAccess="No active subscription" />
					)
				}
			/>
			<EditButton label="Edit" />
			<FunctionField render={(record) => <BillingAccountDeleteListButton record={record} />} />
		</Datagrid>
	</List>
);
